import { ErrorMessage } from '@hookform/error-message'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Flex, Form, Input, Select, Skeleton, Typography, notification } from 'antd'
import { Fragment, useEffect, useState } from 'react'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { generateValidationSchema } from 'src/app/features/feature-business-administration/user-roles/add-user-role/validation'
import RichTextField from 'src/app/shared/components/common/rich-text-field'
import { priGrpTypCodeName } from 'src/app/shared/constants/common.constants'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import {
  fetchPermission,
  fetchPrimaryGroupType,
  fetchSubModuleItem,
  fetchUserNotificationType,
  postCreateNewUserRole,
} from 'src/app/shared/services/UserRoleService'
import {
  getEmailsSetup,
  getPermissionsSetup,
  getPrimaryGroupSetup,
  setUpEmails,
  setUpModules,
  setUpPermissions,
  setUpPrimaryGroupTypes,
} from 'src/app/store/userRoleSetup'
import { DEFAULT_GROUP_TYPE, EXPIRATION_TYPE, contrastBinding } from './constants'
import EmailSwitchers from './EmailSwitchers'
import Expirations from './Expirations'
import NavigationOptions from './NavigationOptions'
import Switchers from './Switchers'
import { SUPER_ADMIN_ONLY_PERMISSIONS } from 'src/app/shared/models/roleAndPermission.model'

const AddUserRole = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const primaryGroupTypes = useSelector(getPrimaryGroupSetup)
  const [defaultGroupType, setDefaultGroupType] = useState<string>('')
  const [defaultPermission, setDefaultPermission] = useState<string>('')
  const permissions = useSelector(getPermissionsSetup)
  // A new role can never be the Super Admin role, so Super-Admin-only permissions are always hidden here.
  const filteredPermissions = permissions?.filter(
    (permission: any) => !SUPER_ADMIN_ONLY_PERMISSIONS.includes(permission?.name),
  )
  const emails = useSelector(getEmailsSetup)
  const [loading, setLoading] = useState<boolean>(false)
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [dependLoading, setDependLoading] = useState<boolean>(false)

  const {
    control,
    formState: { errors },
    resetField,
    handleSubmit,
    getValues,
    setValue,
    clearErrors,
    setError,
    trigger,
  } = useForm<any>({
    mode: 'all',
    defaultValues: async () => await getInitstate(),
    resolver: yupResolver(generateValidationSchema(t)),
  })

  const currentPrimaryGroupType = useWatch({
    control,
    name: 'primaryGroupType',
  })

  const PermissionsWatch = useWatch({
    control,
    name: 'permissions',
  })

  const currentexpirations = useWatch({
    control,
    name: 'expirations',
  })

  const navigationOptionsWatch = useWatch({
    control,
    name: 'navigationOptions',
  })

  const {
    fields: emailFields,
    remove: removeEmail,
    replace,
  } = useFieldArray({
    control,
    name: 'emails',
  })

  const getInitstate = async () => {
    setIsFetching(true)
    setDependLoading(true)
    try {
      const primaryGroupTypeAPI = fetchPrimaryGroupType()

      const [primaryGroupTypeRes] = await Promise.all([primaryGroupTypeAPI])
      if (primaryGroupTypeRes?.data) {
        dispatch(setUpPrimaryGroupTypes(primaryGroupTypeRes?.data))
        setDefaultGroupType(
          primaryGroupTypeRes?.data?.filter((item: any) => item.codeName === DEFAULT_GROUP_TYPE)?.[0]?.id,
        )
      }

      return {
        primaryGroupType: primaryGroupTypeRes?.data?.filter((item: any) => item.codeName === DEFAULT_GROUP_TYPE)?.[0]
          ?.id,
        roleName: '',
        selectAllNavigations: false,
        selectAllPermissions: false,
        selectAllEmails: false,
        navigationOptions: [],
        permissions: [],
        emails: [],
        expirations: EXPIRATION_TYPE.DOES_NOT_EXPIRE,
        expirationMinutes: null,
        expirationDays: null,
      }
    } catch (error) {
      console.log('error', error)
      return {
        primaryGroupType: '',
        roleName: '',
        selectAllNavigations: false,
        selectAllPermissions: false,
        selectAllEmails: false,
        navigationOptions: [],
        permissions: [],
        emails: [],
        expirations: EXPIRATION_TYPE.DOES_NOT_EXPIRE,
        expirationMinutes: null,
        expirationDays: null,
      }
    } finally {
      setIsFetching(false)
    }
  }

  const handleCancel = () => {
    navigate(menuUrls.userRoles)
  }

  const onSubmit = async (data: any) => {
    const payload = {
      primaryGroupTypeId: data.primaryGroupType ?? [],
      userRoleName: data.roleName?.trim() ?? '',
      navigationOptionIds: data.navigationOptions ?? [],
      permissionIds: data.permissions ?? [defaultPermission],
      userNotificationTypeIds: data.emails?.map((item: any) => item?.emailId) ?? [],
      expirationType: data.expirations,
      expirationMinutes: data.expirationMinutes ?? null,
      expirationDays: data.expirationDays ?? null,
    }

    setLoading(true)
    postCreateNewUserRole(payload)
      .then(() => {
        navigate(menuUrls.userRoles)

        notification.success({
          message: (
            <RichTextField
              richText={t(`SystemMessages:${SYSTEM_MESSAGE_IDS.CREATE_ROLE_NAME_SUCCESS}`, {
                Role_Name: data.roleName.trim(),
              })}
            />
          ),

          closeIcon: null,
        })
      })
      .catch((error) => {
        setLoading(false)
        if (error?.fieldError) {
          if (error?.error === 'PP80') {
            setError(contrastBinding[`${error?.fieldError}`] || '', {
              message: t(`SystemMessages:${error?.error}`, {
                Navigation_Options: error?.duplicatedNavigationOptions?.join(', '),
              }),
            })

            return
          }

          if (error?.error === 'PP91') {
            setError(contrastBinding[`${error?.fieldError}`] || '', {
              message: t(`SystemMessages:${error?.error}`),
            })

            return
          }
          setError(contrastBinding[`${error?.fieldError}`] || '', {
            message: t(`SystemMessages:${error.error}`),
          })
        } else {
          notification.error({
            message: t('common:error'),
            description: `${error.error}`,
            placement: 'topRight',
          })
        }
      })
  }
  const isRequiredExpireField = () => {
    switch (currentexpirations) {
      case EXPIRATION_TYPE.EXPIRE_X_MINUTES:
        return !getValues('expirationMinutes')?.trim()
      case EXPIRATION_TYPE.EXPIRE_X_DAYS:
        return !getValues('expirationDays')?.trim()
      default:
        return false
    }
  }
  const isSaveButtonDisabled =
    !!Object.keys(errors)?.length ||
    !getValues('roleName')?.trim() ||
    !getValues('navigationOptions')?.length ||
    isRequiredExpireField()

  useEffect(() => {
    if (navigationOptionsWatch?.length > 0) {
      clearErrors('navigationOptions')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationOptionsWatch])

  useEffect(() => {
    if (currentexpirations !== EXPIRATION_TYPE.EXPIRE_X_MINUTES) {
      resetField('expirationMinutes')
    }
    if (currentexpirations !== EXPIRATION_TYPE.EXPIRE_X_DAYS) {
      resetField('expirationDays')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentexpirations])

  useEffect(() => {
    setDependLoading(true)

    fetchSubModuleItem(currentPrimaryGroupType !== defaultGroupType).then((res: any) => {
      if (res?.data) {
        resetField('selectAllNavigations')
        resetField('selectAllEmails')
        dispatch(setUpModules(res?.data))
        setDependLoading(false)
      }
    })

    fetchUserNotificationType(currentPrimaryGroupType === defaultGroupType ? 'bank' : 'client').then((res: any) => {
      if (res?.data) {
        dispatch(setUpEmails(res?.data))
        setDependLoading(false)
      }
    })

    fetchPermission({
      primaryGroupTypeCodeName: currentPrimaryGroupType === defaultGroupType
        ? priGrpTypCodeName.bankAccess
        : priGrpTypCodeName.clientAccess
    }).then((res: any) => {
      if (res?.data) {
        dispatch(setUpPermissions(res?.data))
        setDependLoading(false)
      }
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPrimaryGroupType])

  return (
    <Form
      name='add-user-role-form'
      className='form-container add-user-role-form'
      layout='vertical'
      onFinish={handleSubmit(onSubmit)}
    >
      {isFetching || dependLoading ? (
        <Skeleton />
      ) : (
        <Fragment>
          <Form.Item label={t('UserRoles:primaryGroupType.title')}>
            <Controller
              name='primaryGroupType'
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  options={primaryGroupTypes?.filter((item: any) => item?.codeName !== priGrpTypCodeName.clientAccess)
                    .map((item: any) => ({
                    ...item,
                    value: item?.id,
                    label: item?.name
                  }))}
                  placeholder={t('UserRoles:primaryGroupType.placeholder')}
                  onChange={(e) => {
                    field.onChange(e)

                    if (navigationOptionsWatch?.length > 0) {
                      resetField('navigationOptions')
                    }
                    if (emailFields?.length > 0) {
                      removeEmail()
                    }
                  }}
                />
              )}
            />
          </Form.Item>

          <Form.Item label={t('UserRoles:roleName.title')} className='isRequired'>
            <Controller
              name='roleName'
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  status={errors?.roleName ? 'error' : ''}
                  placeholder={t('UserRoles:roleName.placeholder')}
                  onChange={(e) => {
                    let e1 = e
                    e1.target.value = e.target.value.trimStart()
                    field.onChange(e1)
                    trigger('roleName')
                  }}
                  onBlur={(e) => {
                    let e1 = e
                    e1.target.value = e.target.value.trim()
                    field.onChange(e1)
                  }}
                  maxLength={40}
                />
              )}
            />

            <Typography className='error-message'>
              <ErrorMessage errors={errors} name='roleName' />
            </Typography>
          </Form.Item>

          <NavigationOptions
            control={control}
            errors={errors}
            getValues={getValues}
            setValue={setValue}
            navigationOptionsWatch={navigationOptionsWatch}
            trigger={trigger}
          />

          <Switchers
            listSwitchers={filteredPermissions}
            title={t('UserRoles:permissions')}
            name='permissions'
            control={control}
            errors={errors}
            getValues={getValues}
            setValue={setValue}
            isTurnOnFirst
            data={PermissionsWatch}
            resetField={resetField}
            trigger={trigger}
            allOptionName='selectAllPermissions'
            defaultPermission={defaultPermission}
          />

          <EmailSwitchers
            listSwitchers={emails}
            title={t('UserRoles:emails')}
            data={emailFields}
            replace={replace}
            control={control}
            setValue={setValue}
            trigger={trigger}
            name='emails'
          />

          <Expirations control={control} errors={errors} trigger={trigger} clearErrors={clearErrors} />
          <Flex className='button-group' justify='flex-end' gap={24} style={{ zIndex: 1000 }}>
            <Button className='cancel-button' onClick={handleCancel}>
              {t('UserRoles:cancel')}
            </Button>

            <Button
              className='save-button'
              type='primary'
              disabled={isSaveButtonDisabled}
              htmlType='button'
              onClick={handleSubmit((data) => onSubmit(data))}
              loading={loading}
              style={loading ? { width: 200 } : {}}
            >
              {t('UserRoles:saveUserRole')}
            </Button>
          </Flex>
        </Fragment>
      )}
    </Form>
  )
}

export default AddUserRole
